import React, { useEffect, useState } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { inputCls, labelCls } from "@/components/common/states";
import { PUNE_LOCATIONS, PROPERTY_TYPES, STATUS_OPTIONS, priceRange, STATUS_LABEL, formatCr } from "@/lib/constants";
import { Plus, Star, Eye, EyeOff, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const BLANK = {
  name: "", location_slug: "wakad", property_type: "residential", status: "under-construction",
  developer_name: "", price_min: "", price_max: "", area_min: "", area_max: "", area_unit: "sq.ft.",
  configuration: "", possession: "", rera_number: "Available on enquiry", description: "",
  images: "", amenities: "", featured: false, active: true,
};

export default function AdminProperties() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  useSEO({ title: "Properties · Admin" });

  const load = () => apiGet("/properties", { limit: 100 }).then((d) => setItems(d.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const startCreate = () => setEditing({ ...BLANK });
  const startEdit = (p) => setEditing({
    ...p,
    configuration: (p.configuration || []).join(", "),
    images: (p.images || []).join(", "),
    amenities: (p.amenities || []).join(", "),
  });

  const toggle = async (p, field) => {
    await apiPut(`/admin/properties/${p.slug}`, { [field]: !p[field] });
    toast.success("Updated");
    load();
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await apiDelete(`/admin/properties/${p.slug}`);
    toast.success("Deleted");
    load();
  };

  const save = async () => {
    const e = editing;
    if (!e.name.trim()) return toast.error("Name is required");
    const payload = {
      ...e,
      location_name: PUNE_LOCATIONS.find((l) => l.toLowerCase().replace(/\s/g, "-") === e.location_slug) || e.location_slug,
      price_min: e.price_min ? Number(e.price_min) : null,
      price_max: e.price_max ? Number(e.price_max) : null,
      price_display: e.price_min ? `${formatCr(Number(e.price_min))} - ${formatCr(Number(e.price_max))}` : "Price on request",
      area_min: e.area_min ? Number(e.area_min) : null,
      area_max: e.area_max ? Number(e.area_max) : null,
      configuration: e.configuration.split(",").map((x) => x.trim()).filter(Boolean),
      bedrooms: e.configuration.split(",").map((x) => parseInt(x)).filter((n) => !isNaN(n)),
      images: e.images.split(",").map((x) => x.trim()).filter(Boolean),
      amenities: e.amenities.split(",").map((x) => x.trim()).filter(Boolean),
      highlights: e.highlights || [],
    };
    const isNew = !items.find((it) => it.slug === e.slug);
    const res = isNew ? await apiPost("/admin/properties", payload) : await apiPut(`/admin/properties/${e.slug}`, payload);
    if (res.success) { toast.success(isNew ? "Property created" : "Property updated"); setEditing(null); load(); }
    else toast.error(res.error?.message || "Save failed");
  };

  const set = (k) => (ev) => setEditing((s) => ({ ...s, [k]: ev.target.type === "checkbox" ? ev.target.checked : ev.target.value }));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-3xl text-foreground">Properties</h1><p className="text-muted-foreground">{items?.length ?? 0} listings</p></div>
        <Button data-testid="add-property" onClick={startCreate} className="bg-primary text-primary-foreground gap-2"><Plus className="h-4 w-4" aria-hidden /> Add Property</Button>
      </div>

      <div className="rounded-lg border border-border bg-white overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left p-4">Name</th><th className="text-left p-4">Location</th><th className="text-left p-4">Price</th><th className="text-left p-4">Status</th><th className="text-left p-4">Flags</th><th className="text-right p-4">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-border text-sm">
            {items?.map((p) => (
              <tr key={p.slug} data-testid={`prop-row-${p.slug}`}>
                <td className="p-4 font-medium text-foreground">{p.name}{p.is_demo && <span className="ml-2 text-[10px] uppercase text-accent">demo</span>}</td>
                <td className="p-4 text-muted-foreground">{p.location_name}</td>
                <td className="p-4 text-muted-foreground">{priceRange(p)}</td>
                <td className="p-4 text-muted-foreground">{STATUS_LABEL[p.status] || p.status}</td>
                <td className="p-4">
                  <div className="flex gap-1.5">
                    <button data-testid={`feature-${p.slug}`} onClick={() => toggle(p, "featured")} title="Feature" className={`h-8 w-8 grid place-items-center rounded-sm border ${p.featured ? "bg-accent/15 border-accent text-accent" : "border-border text-muted-foreground"}`}><Star className="h-4 w-4" aria-hidden /></button>
                    <button data-testid={`publish-${p.slug}`} onClick={() => toggle(p, "active")} title="Publish" className={`h-8 w-8 grid place-items-center rounded-sm border ${p.active ? "bg-green-50 border-green-300 text-green-600" : "border-border text-muted-foreground"}`}>{p.active ? <Eye className="h-4 w-4" aria-hidden /> : <EyeOff className="h-4 w-4" aria-hidden />}</button>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex justify-end gap-1.5">
                    <button data-testid={`edit-${p.slug}`} onClick={() => startEdit(p)} className="h-8 w-8 grid place-items-center rounded-sm border border-border text-primary hover:bg-muted"><Pencil className="h-4 w-4" aria-hidden /></button>
                    <button data-testid={`delete-${p.slug}`} onClick={() => remove(p)} className="h-8 w-8 grid place-items-center rounded-sm border border-border text-destructive hover:bg-destructive/5"><Trash2 className="h-4 w-4" aria-hidden /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">{editing.slug ? "Edit" : "New"} Property</DialogTitle></DialogHeader>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><label className={labelCls}>Name *</label><input data-testid="pf-name" value={editing.name} onChange={set("name")} className={inputCls} /></div>
                <div><label className={labelCls}>Location</label><select value={editing.location_slug} onChange={set("location_slug")} className={inputCls}>{PUNE_LOCATIONS.map((l) => <option key={l} value={l.toLowerCase().replace(/\s/g, "-")}>{l}</option>)}</select></div>
                <div><label className={labelCls}>Type</label><select value={editing.property_type} onChange={set("property_type")} className={inputCls}>{PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
                <div><label className={labelCls}>Status</label><select value={editing.status} onChange={set("status")} className={inputCls}>{STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</select></div>
                <div><label className={labelCls}>Developer</label><input value={editing.developer_name} onChange={set("developer_name")} className={inputCls} /></div>
                <div><label className={labelCls}>Price Min (Cr)</label><input value={editing.price_min} onChange={set("price_min")} className={inputCls} placeholder="0.95" /></div>
                <div><label className={labelCls}>Price Max (Cr)</label><input value={editing.price_max} onChange={set("price_max")} className={inputCls} placeholder="1.65" /></div>
                <div><label className={labelCls}>Area Min (sq.ft.)</label><input value={editing.area_min} onChange={set("area_min")} className={inputCls} /></div>
                <div><label className={labelCls}>Area Max (sq.ft.)</label><input value={editing.area_max} onChange={set("area_max")} className={inputCls} /></div>
                <div><label className={labelCls}>Configuration (comma)</label><input value={editing.configuration} onChange={set("configuration")} className={inputCls} placeholder="2 BHK, 3 BHK" /></div>
                <div><label className={labelCls}>Possession</label><input value={editing.possession} onChange={set("possession")} className={inputCls} placeholder="Dec 2027" /></div>
                <div><label className={labelCls}>RERA</label><input value={editing.rera_number} onChange={set("rera_number")} className={inputCls} /></div>
                <div className="sm:col-span-2"><label className={labelCls}>Image URLs (comma)</label><input value={editing.images} onChange={set("images")} className={inputCls} /></div>
                <div className="sm:col-span-2"><label className={labelCls}>Amenities (comma)</label><input value={editing.amenities} onChange={set("amenities")} className={inputCls} /></div>
                <div className="sm:col-span-2"><label className={labelCls}>Description</label><textarea value={editing.description} onChange={set("description")} rows={3} className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" /></div>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.featured} onChange={set("featured")} /> Featured</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active} onChange={set("active")} /> Published</label>
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button data-testid="pf-save" onClick={save} className="bg-primary text-primary-foreground">Save</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
